import axiosInstance from './axiosConfig';
import { ImageStyle, ImageStyleCategoryUser, ImageStylesResponse, PlatformType } from './imageGenerationService';

/**
 * Carousel styles are an independent resource (backed by the carousel_styles
 * table), but they are returned in the SAME shape as banner/material styles so
 * the existing StyleSelectModal can render them via its `fetchFunctions` prop.
 * Only the data source differs — the UI is identical to the banner page.
 */

// Raw API response types (from backend CarouselStyleController)
interface RawCarouselStyle {
  id: number;
  name: string;
  prompt_template: string;
  thumbnail_url?: string;
  description: string;
  sort_order: number;
  platforms?: PlatformType[];
}

interface GroupedCarouselStyles {
  category: string;
  styles: RawCarouselStyle[];
}

interface RawCarouselStylesResponse {
  success: boolean;
  data: {
    categories: string[];
    grouped_styles: GroupedCarouselStyles[];
    total_count: number;
  };
}

interface CarouselStyleCategoriesResponse {
  success: boolean;
  data: ImageStyleCategoryUser[];
}

const carouselStyleService = {
  /**
   * Get all active carousel styles.
   * Returns the same ImageStylesResponse format as imageGenerationService.getStyles()
   * so it can be passed directly to StyleSelectModal's fetchFunctions.
   */
  async getStyles(): Promise<ImageStylesResponse> {
    const response = await axiosInstance.get<RawCarouselStylesResponse>('/carousel-styles');

    const flatStyles: ImageStyle[] = [];

    if (response.data.success && response.data.data?.grouped_styles) {
      for (const group of response.data.data.grouped_styles) {
        for (const style of group.styles) {
          flatStyles.push({
            id: style.id,
            name: style.name,
            description: style.description || '',
            category: group.category,
            platforms: style.platforms || [],
            aspect_ratio: '1:1',
            style_prompt: style.prompt_template || '',
            example_image_url: style.thumbnail_url,
            is_active: true,
            is_favorited: false,
            created_at: '',
            updated_at: '',
          });
        }
      }
    }

    return {
      success: response.data.success,
      data: flatStyles,
    };
  },

  /**
   * Get all active carousel style categories.
   */
  async getStyleCategories(): Promise<ImageStyleCategoryUser[]> {
    const response = await axiosInstance.get<CarouselStyleCategoriesResponse>('/carousel-styles/categories');
    return response.data.data || [];
  },
};

export default carouselStyleService;
